(() => {
  if (window.superDropGallery) return;

  const instances = new Map();
  const imageCache = new Map();
  const manifestCache = new Map();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const printOrder = ['blank', 'custom'];
  const productHandles = ['oxblood-hoodie', 'oxblood-pants', 'ember-hoodie', 'ember-pants'];
  const preferenceKey = 'super-drop-preference-v1';
  const waitlistForms = new WeakSet();
  const selectionTags = new Set([
    ...productHandles.map((handle) => `product-${handle}`),
    ...[...printOrder, 'super-1', 'super-2', 'jesus-1', 'jesus-2'].map((id) => `print-${id}`),
    ...['blank', 'stamp', 'cross', 'halo', 'slant', 'ember-crown', 'ember-risen', 'ember-slant', 'ember-stamp', 'custom'].map((id) => `back-${id}`),
    'garment-hoodie', 'garment-pants', 'color-ember', 'color-oxblood', 'back-print-interest'
  ]);

  const validPreference = (value) => {
    if (!value || !productHandles.includes(value.product)) return null;
    return {
      product: value.product,
      print: value.product.endsWith('-hoodie') && printOrder.includes(value.print) ? value.print : 'blank'
    };
  };

  const savePreference = (value) => {
    const preference = validPreference(value);
    if (preference) {
      try { sessionStorage.setItem(preferenceKey, JSON.stringify(preference)); } catch {}
    }
    return preference;
  };

  const readPreference = () => {
    const query = new URLSearchParams(window.location.search);
    if (query.has('product')) return savePreference({ product: query.get('product'), print: query.get('print') });
    try { return savePreference(JSON.parse(sessionStorage.getItem(preferenceKey))); } catch { return null; }
  };

  const connectWaitlist = () => {
    document.querySelectorAll('.s-waitlist__form').forEach((form) => {
      if (waitlistForms.has(form)) return;
      const field = form.querySelector('[name="contact[tags]"]');
      if (!field) return;
      waitlistForms.add(form);
      const apply = () => {
        const preference = readPreference();
        const [color, garment] = preference?.product.split('-') || [];
        const tags = new Set((field.value || '').split(',').map((tag) => tag.trim()).filter(Boolean));
        [...tags].filter((tag) => selectionTags.has(tag)).forEach((tag) => tags.delete(tag));
        tags.add('waitlist');
        if (preference) {
          tags.add(`product-${preference.product}`);
          tags.add(`garment-${garment}`);
          tags.add(`color-${color}`);
        }
        if (garment === 'hoodie') {
          tags.add(`print-${preference.print}`);
          if (preference.print === 'custom') { tags.add('back-print-interest'); tags.add('back-custom'); }
        }
        field.value = [...tags].join(',');
      };
      form.addEventListener('change', () => queueMicrotask(apply));
      form.addEventListener('submit', apply);
      form.addEventListener('formdata', (event) => {
        apply();
        event.formData.set('contact[tags]', field.value);
      });
      apply();
    });
  };

  const safeUrl = (value) => {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      const url = new URL(value, window.location.href);
      return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
    } catch { return ''; }
  };

  const renditionWidth = (root) => {
    const viewport = window.innerWidth || 1200;
    const mobile = viewport <= 760;
    const isHome = document.body?.classList.contains('template-index');
    const displayed = root.dataset.dropMode === 'product'
      ? Math.min(viewport - (mobile ? 32 : 80), 740)
      : isHome
        ? Math.min((viewport - (mobile ? 46 : 130)) / 2, 480)
        : Math.min((viewport - 48) * (mobile ? .88 : .45), 530);
    const pixels = Math.max(1, displayed) * Math.min(window.devicePixelRatio || 1, 2);
    return [320, 480, 640, 800, 1000, 1254].find((width) => width >= pixels) || 1254;
  };

  const renditionUrl = (value, width) => {
    const source = safeUrl(value);
    if (!source) return '';
    const url = new URL(source);
    if (url.hostname === 'cdn.shopify.com' || (url.origin === window.location.origin && url.pathname.startsWith('/cdn/shop/'))) {
      url.searchParams.set('width', String(width));
    }
    return url.href;
  };

  const loadImage = (value) => {
    const url = safeUrl(value);
    if (!url) return Promise.reject(new Error('Missing image'));
    if (imageCache.has(url)) return imageCache.get(url);
    const promise = new Promise((resolve, reject) => {
      const image = new Image();
      image.decoding = 'async';
      const timeout = window.setTimeout(() => finish(new Error('Image timeout')), 15000);
      const finish = (error) => {
        window.clearTimeout(timeout);
        image.onload = null;
        image.onerror = null;
        if (error) reject(error);
        else resolve(image);
      };
      image.onload = async () => {
        try { await image.decode(); } catch {}
        image.naturalWidth ? finish() : finish(new Error('Image unavailable'));
      };
      image.onerror = () => finish(new Error('Image unavailable'));
      image.src = url;
    });
    imageCache.set(url, promise);
    promise.catch(() => imageCache.delete(url));
    return promise;
  };

  const readManifest = (url) => {
    if (!url) return Promise.resolve({});
    if (manifestCache.has(url)) return manifestCache.get(url);
    const promise = (async () => {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 5000);
      try {
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) return {};
        return await response.json();
      } catch { return {}; }
      finally { window.clearTimeout(timeout); }
    })();
    manifestCache.set(url, promise);
    return promise;
  };

  const validBox = (box, fallback = { left: 34, top: 32, width: 32, height: 40 }) => {
    if (!box || typeof box !== 'object') return fallback;
    const result = Object.fromEntries(['left', 'top', 'width', 'height'].map((key) => [key, Number(box[key])]));
    if (Object.values(result).some((value) => !Number.isFinite(value))) return fallback;
    if (result.left < 0 || result.top < 0 || result.width <= 0 || result.height <= 0) return fallback;
    if (result.left + result.width > 100 || result.top + result.height > 100) return fallback;
    return result;
  };

  const mergeProduct = (fallback, supplied = {}, width = 1254) => {
    const product = { ...fallback };
    for (const view of ['front', 'back', 'side']) product[view] = renditionUrl(safeUrl(supplied[view]) || safeUrl(fallback[view]), width);
    const fallbackBox = validBox(fallback.printBox);
    const suppliedBox = validBox(supplied.printBox, fallbackBox);
    product.printBox = Math.abs(suppliedBox.height / suppliedBox.width - 1.25) < .001 ? suppliedBox : fallbackBox;
    delete product.prints;
    delete product.printLabels;
    return product;
  };

  const interpolate = (text, values) => Object.entries(values).reduce((result, [key, value]) => result.replaceAll(`__${key}__`, String(value)), text || '');
  const settle = (promises) => Promise.allSettled(promises);

  class GarmentLayer {
    constructor(element) {
      this.images = [...element.querySelectorAll('[data-view-image]')];
      this.active = 0;
      this.intent = 0;
    }

    async show(src, alt) {
      const intent = ++this.intent;
      const current = this.images[this.active];
      if ((current.currentSrc === src || current.src === src) && current.complete && current.naturalWidth) {
        current.alt = alt;
        return true;
      }
      try {
        await loadImage(src);
        if (intent !== this.intent) return false;
        const next = this.active === 0 ? 1 : 0;
        const image = this.images[next];
        image.removeAttribute('srcset');
        image.src = src;
        image.hidden = false;
        try { await image.decode(); } catch {}
        if (intent !== this.intent) return false;
        this.images.forEach((item, index) => {
          const selected = index === next;
          item.classList.toggle('is-active', selected);
          item.alt = selected ? alt : '';
          item.setAttribute('aria-hidden', String(!selected));
        });
        this.active = next;
        return true;
      } catch { return false; }
    }

    destroy() { this.intent += 1; }
  }

  class Collection {
    constructor(root, config) {
      this.root = root;
      this.config = config;
      this.signal = new AbortController();
      this.cards = [...root.querySelectorAll('[data-drop-card]')].map((element) => ({ element, product: config.products[element.dataset.dropCard] }));
      this.track = root.querySelector('[data-drop-track]');
      this.destroyed = false;
      const options = { signal: this.signal.signal };
      this.cards.forEach((card) => {
        card.element.addEventListener('pointerenter', () => { if (finePointer.matches) this.prepareFront(card); }, options);
        card.element.addEventListener('focusin', () => this.prepareFront(card), options);
      });
      if (typeof IntersectionObserver === 'function') {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const card = this.cards.find((item) => item.element === entry.target);
              if (card) this.prepare(card);
            }
          });
        }, { rootMargin: '200px 0px', threshold: 0 });
        this.cards.forEach((card) => this.observer.observe(card.element));
      } else this.cards.forEach((card) => this.prepare(card));
      root.querySelector('[data-gallery-tools]').hidden = false;
      root.querySelector('[data-gallery-prev]').addEventListener('click', () => this.navigate(-1), options);
      root.querySelector('[data-gallery-next]').addEventListener('click', () => this.navigate(1), options);
      this.track.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = null; this.updateNavigation(); });
      }, { ...options, passive: true });
      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(() => this.updateNavigation());
        this.resizeObserver.observe(this.track);
      } else window.addEventListener('resize', () => this.updateNavigation(), options);
      this.updateNavigation();
    }

    async prepareFront(card) {
      if (card.frontPrepared) return;
      card.frontPrepared = true;
      const intent = card.frontIntent = (card.frontIntent || 0) + 1;
      const source = card.product.front;
      try {
        await loadImage(source);
        if (this.destroyed || intent !== card.frontIntent) return;
        const image = card.element.querySelector('[data-card-front]');
        image.removeAttribute('srcset');
        image.src = source;
        try { await image.decode(); } catch {}
        if (this.destroyed || intent !== card.frontIntent) return;
        if (!image.complete || !image.naturalWidth) throw new Error('Front image unavailable');
        card.element.classList.add('is-front-ready');
      } catch { if (intent === card.frontIntent) card.frontPrepared = false; }
    }

    async prepare(card) {
      if (finePointer.matches) this.prepareFront(card);
      if (card.prepared) return;
      card.prepared = true;
      const source = card.product.back;
      try {
        await loadImage(source);
        if (this.destroyed || source !== card.product.back) return;
        const image = card.element.querySelector('[data-card-back]');
        image.removeAttribute('srcset');
        image.src = source;
      } catch { if (source === card.product.back) card.prepared = false; }
    }

    navigate(direction) {
      if (!this.cards.length) return;
      const step = this.cards[0].element.getBoundingClientRect().width + (parseFloat(getComputedStyle(this.track).columnGap) || 0);
      this.track.scrollBy({ left: step * direction, behavior: motion.matches ? 'instant' : 'smooth' });
    }

    updateNavigation() {
      if (!this.cards.length) return;
      const maximum = this.track.scrollWidth - this.track.clientWidth;
      const position = this.track.scrollLeft;
      this.root.querySelector('[data-gallery-navigation]').hidden = maximum < 3;
      this.root.querySelector('[data-gallery-prev]').disabled = position < 3;
      this.root.querySelector('[data-gallery-next]').disabled = position > maximum - 3;
      const cardWidth = this.cards[0].element.getBoundingClientRect().width;
      const current = Math.min(this.cards.length, Math.max(1, Math.round(position / (cardWidth + (parseFloat(getComputedStyle(this.track).columnGap) || 0))) + 1));
      this.root.querySelector('[data-gallery-progress]').textContent = interpolate(this.config.labels.progress, { CURRENT: current, TOTAL: this.cards.length });
    }

    destroy() {
      this.destroyed = true;
      this.signal.abort();
      cancelAnimationFrame(this.scrollFrame);
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
    }
  }

  const customLayout = (width, height, scale = 100, horizontal = 50, vertical = 0) => {
    const bounded = (value, min, max, fallback) => Number.isFinite(Number(value)) ? Math.max(min, Math.min(max, Number(value))) : fallback;
    const factor = Math.min(16 / width, 20 / height) * bounded(scale, 25, 100, 100) / 100;
    const widthInches = width * factor, heightInches = height * factor;
    const w = widthInches / 16 * 100, h = heightInches / 20 * 100;
    return {
      width: w, height: h,
      left: (100 - w) * bounded(horizontal, 0, 100, 50) / 100,
      top: (100 - h) * bounded(vertical, 0, 100, 0) / 100,
      widthInches, heightInches
    };
  };

  const supportedImage = async (file) => {
    if (!file || !file.size || file.size > 20 * 1024 * 1024) return false;
    if (file.type && !['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) return false;
    const bytes = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    return (bytes[0] === 137 && bytes[1] === 80 && bytes[2] === 78 && bytes[3] === 71 && bytes[4] === 13 && bytes[5] === 10 && bytes[6] === 26 && bytes[7] === 10)
      || (bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255)
      || (String.fromCharCode(...bytes.slice(0, 4)) === 'RIFF' && String.fromCharCode(...bytes.slice(8, 12)) === 'WEBP');
  };

  class ProductDetail {
    constructor(root, config) {
      this.root = root;
      this.config = config;
      this.product = config.products[root.dataset.productHandle];
      this.signal = new AbortController();
      this.destroyed = false;
      this.intent = 0;
      this.uploadIntent = 0;
      this.localUrls = new Set();
      this.custom = null;
      this.placement = { scale: 100, horizontal: 50, vertical: 0 };
      this.view = 'front';
      this.print = 'blank';
      this.stage = root.querySelector('[data-product-stage]');
      this.garment = new GarmentLayer(this.stage);
      this.customRegion = root.querySelector('[data-custom-region]');
      this.customImage = root.querySelector('[data-custom-print]');
      this.customControls = root.querySelector('[data-custom-controls]');
      this.fileInput = root.querySelector('[data-custom-file]');
      this.uploadStatus = root.querySelector('[data-custom-status]');
      this.layoutControls = root.querySelector('[data-custom-layout]');
      this.views = ['front', ...(this.product.type === 'hoodie' && this.product.side ? ['side'] : []), 'back'];
      const options = { signal: this.signal.signal };

      root.querySelector('[data-drop-waitlist]').addEventListener('click', () => savePreference({ product: this.product.handle, print: this.print }), options);
      root.querySelector('[data-view-controls]').hidden = false;
      root.querySelectorAll('[data-product-view]').forEach((button) => {
        button.hidden = !this.views.includes(button.dataset.productView);
        button.addEventListener('click', () => { this.view = button.dataset.productView; this.render(); }, options);
      });
      const printControls = root.querySelector('[data-print-controls]');
      if (printControls && this.product.type === 'hoodie') {
        printControls.hidden = false;
        printControls.addEventListener('change', (event) => {
          if (!event.target.matches('input[type="radio"]')) return;
          this.print = event.target.value === 'custom' ? 'custom' : 'blank';
          this.view = 'back';
          this.render();
        }, options);
        this.fileInput.addEventListener('change', () => {
          const file = this.fileInput.files?.[0];
          if (file) this.upload(file);
        }, options);
        root.querySelectorAll('[data-custom-adjust]').forEach((input) => input.addEventListener('input', () => {
          const key = input.dataset.customAdjust;
          if (['scale', 'horizontal', 'vertical'].includes(key)) this.placement[key] = Number(input.value);
          this.updateCustomArt();
        }, options));
        root.querySelector('[data-custom-reset]').addEventListener('click', () => { this.resetPlacement(); this.updateCustomArt(); }, options);
        root.querySelector('[data-custom-remove]').addEventListener('click', () => {
          this.uploadIntent += 1;
          this.cancelUpload?.();
          this.clearCustom();
          this.setUploadStatus('customEmpty');
          this.updateCustomArt();
        }, options);
      }
      if (this.product.type === 'hoodie' && new URLSearchParams(window.location.search).get('print') === 'custom') {
        this.print = 'custom';
        this.view = 'back';
      }
      this.stage.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1) { this.touchStart = null; return; }
        const touch = event.touches[0];
        this.touchStart = { id: touch.identifier, x: touch.clientX, y: touch.clientY };
      }, { ...options, passive: true });
      this.stage.addEventListener('touchmove', (event) => {
        if (event.touches.length !== 1) this.touchStart = null;
      }, { ...options, passive: true });
      this.stage.addEventListener('touchend', (event) => {
        const start = this.touchStart;
        this.touchStart = null;
        if (!start || event.touches.length !== 0 || event.changedTouches.length !== 1) return;
        const touch = event.changedTouches[0];
        if (touch.identifier !== start.id) return;
        const dx = touch.clientX - start.x;
        const dy = touch.clientY - start.y;
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        const next = (this.views.indexOf(this.view) + (dx < 0 ? 1 : -1) + this.views.length) % this.views.length;
        this.view = this.views[next];
        this.render();
      }, { ...options, passive: true });
      this.stage.addEventListener('touchcancel', () => { this.touchStart = null; }, options);
      this.prewarm();
      this.render(false);
    }

    prewarm() {
      const views = [this.product.front, this.product.side, this.product.back];
      if (finePointer.matches && (window.innerWidth || 1200) > 760) {
        const peer = this.config.products[`${this.product.color === 'ember' ? 'oxblood' : 'ember'}-${this.product.type}`];
        views.push(peer.front, peer.back);
      }
      settle(views.filter(Boolean).map(loadImage));
    }

    setUploadStatus(key, values = {}) {
      if (this.uploadStatus) this.uploadStatus.textContent = interpolate(this.config.labels[key], values);
    }

    retireUrl(url) {
      if (!this.localUrls.has(url)) return;
      if (this.customImage?.getAttribute('src') === url) this.customImage.removeAttribute('src');
      this.localUrls.delete(url);
      URL.revokeObjectURL(url);
    }

    clearCustom() {
      if (this.custom) this.retireUrl(this.custom.src);
      this.custom = null;
      if (this.customImage) { this.customImage.hidden = true; this.customImage.removeAttribute('src'); }
      if (this.layoutControls) this.layoutControls.hidden = true;
      if (this.fileInput) this.fileInput.value = '';
      this.resetPlacement();
    }

    resetPlacement() {
      this.placement = { scale: 100, horizontal: 50, vertical: 0 };
      this.root.querySelectorAll('[data-custom-adjust]').forEach((input) => { input.value = String(this.placement[input.dataset.customAdjust]); });
    }

    async upload(file) {
      const intent = ++this.uploadIntent;
      this.cancelUpload?.();
      this.setUploadStatus('customLoading');
      let url = '';
      try {
        if (!await supportedImage(file)) throw new Error('customFileError');
        if (this.destroyed || intent !== this.uploadIntent) return;
        url = URL.createObjectURL(file);
        this.localUrls.add(url);
        const image = await new Promise((resolve, reject) => {
          const probe = new Image();
          let finished = false;
          const finish = (error) => {
            if (finished) return;
            finished = true;
            window.clearTimeout(timer);
            probe.onload = null;
            probe.onerror = null;
            this.cancelUpload = null;
            if (error) { probe.src = ''; reject(error); }
            else resolve(probe);
          };
          const timer = window.setTimeout(() => finish(new Error('customDecodeError')), 15000);
          this.cancelUpload = () => finish(new Error('cancelled'));
          probe.onload = () => {
            if (!probe.naturalWidth || !probe.naturalHeight) finish(new Error('customDecodeError'));
            else if (probe.naturalWidth * probe.naturalHeight > 40000000) finish(new Error('customResolutionError'));
            else finish();
          };
          probe.onerror = () => finish(new Error('customDecodeError'));
          probe.src = url;
        });
        if (this.destroyed || intent !== this.uploadIntent) { this.retireUrl(url); return; }
        if (this.custom) this.retireUrl(this.custom.src);
        this.custom = { src: url, width: image.naturalWidth, height: image.naturalHeight };
        this.resetPlacement();
        this.setUploadStatus('customReady', { NAME: file.name });
        this.layoutControls.hidden = false;
        if (this.print === 'custom') { this.view = 'back'; this.render(); }
        else this.updateCustomArt();
      } catch (error) {
        if (url) this.retireUrl(url);
        if (!this.destroyed && intent === this.uploadIntent && error.message !== 'cancelled') {
          this.setUploadStatus(['customFileError', 'customDecodeError', 'customResolutionError'].includes(error.message) ? error.message : 'customDecodeError');
        }
      } finally {
        if (!this.destroyed && intent === this.uploadIntent) this.fileInput.value = '';
      }
    }

    updateCustomArt() {
      if (!this.customRegion || this.product.type !== 'hoodie') return;
      Object.entries(this.product.printBox).forEach(([property, value]) => this.customRegion.style.setProperty(property, `${value}%`));
      const visible = this.view === 'back' && this.print === 'custom' && this.renderedView === 'back';
      this.customRegion.hidden = !visible;
      this.customControls.hidden = this.print !== 'custom';
      this.customImage.hidden = !this.custom;
      if (!this.custom) return;
      const box = customLayout(this.custom.width, this.custom.height, this.placement.scale, this.placement.horizontal, this.placement.vertical);
      ['left', 'top', 'width', 'height'].forEach((property) => this.customImage.style.setProperty(property, `${box[property]}%`));
      if (this.customImage.getAttribute('src') !== this.custom.src) this.customImage.src = this.custom.src;
      this.root.querySelectorAll('[data-custom-adjust]').forEach((input) => {
        if (input.dataset.customAdjust === 'horizontal') input.disabled = box.width >= 99.999;
        if (input.dataset.customAdjust === 'vertical') input.disabled = box.height >= 99.999;
      });
      this.root.querySelector('[data-custom-dimensions]').textContent = interpolate(this.config.labels.customDimensions, { WIDTH: Number(box.widthInches.toFixed(1)), HEIGHT: Number(box.heightInches.toFixed(1)) });
    }

    updateControls() {
      this.root.querySelectorAll('[data-product-view]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.productView === this.view)));
      this.root.querySelectorAll('[data-print-controls] input[type="radio"]').forEach((input) => { input.checked = input.value === this.print; });
      this.root.querySelector('[data-view-count]').textContent = `${String(this.views.indexOf(this.view) + 1).padStart(2, '0')} / ${String(this.views.length).padStart(2, '0')}`;
      const link = this.root.querySelector('[data-drop-waitlist]');
      const url = new URL(link.href, window.location.href);
      url.searchParams.set('product', this.product.handle);
      url.searchParams.set('print', this.print);
      link.href = url.href;
      if (this.customControls) this.customControls.hidden = this.print !== 'custom';
      savePreference({ product: this.product.handle, print: this.print });
    }

    async render(announce = true) {
      const intent = ++this.intent;
      this.garment.intent += 1;
      const view = this.view;
      const print = this.print;
      if (this.customRegion) this.customRegion.hidden = true;
      const error = this.root.querySelector('[data-product-error]');
      this.updateControls();
      this.stage.setAttribute('aria-busy', 'true');
      error.hidden = true;
      const alt = interpolate(this.config.labels.imageAlt, { PRODUCT: this.product.name, VIEW: this.config.labels[view] });
      const loaded = await this.garment.show(this.product[view], alt);
      if (intent !== this.intent || this.destroyed) return;
      this.stage.removeAttribute('aria-busy');
      if (!loaded) {
        this.renderedView = null;
        error.textContent = this.config.labels.error;
        error.hidden = false;
      } else {
        this.renderedView = view;
        this.updateCustomArt();
        if (announce) this.root.querySelector('[data-view-status]').textContent = interpolate(this.config.labels.imageStatus, { VIEW: this.config.labels[view], PRINT: view === 'back' ? this.config.labels[print] : this.config.labels.blank });
      }
    }

    destroy() {
      this.destroyed = true;
      this.intent += 1;
      this.uploadIntent += 1;
      this.signal.abort();
      this.cancelUpload?.();
      this.clearCustom();
      [...this.localUrls].forEach((url) => this.retireUrl(url));
      this.garment.destroy();
    }
  }

  const initialize = async (root) => {
    if (instances.has(root)) return;
    instances.set(root, null);
    try {
      const config = JSON.parse(root.querySelector('[data-drop-config]').textContent);
      const width = renditionWidth(root);
      for (const handle of productHandles) config.products[handle] = mergeProduct(config.products[handle], {}, width);
      const instance = root.dataset.dropMode === 'product'
        ? (config.products[root.dataset.productHandle] && root.querySelector('[data-product-stage]') ? new ProductDetail(root, config) : null)
        : new Collection(root, config);
      instances.set(root, instance);
      const manifest = await readManifest(safeUrl(root.dataset.manifestUrl));
      if (!root.isConnected || instances.get(root) !== instance || instance?.destroyed) return;
      const changed = new Set();
      for (const handle of productHandles) {
        const product = config.products[handle];
        const updated = mergeProduct(product, manifest?.products?.[handle], width);
        if (JSON.stringify(product) !== JSON.stringify(updated)) {
          Object.assign(product, updated);
          changed.add(handle);
        }
      }
      if (instance instanceof ProductDetail && changed.has(instance.product.handle)) {
        instance.views = ['front', ...(instance.product.type === 'hoodie' && instance.product.side ? ['side'] : []), 'back'];
        if (!instance.views.includes(instance.view)) instance.view = 'front';
        instance.root.querySelectorAll('[data-product-view]').forEach(button => { button.hidden = !instance.views.includes(button.dataset.productView); });
        instance.prewarm();
        instance.render(false);
      } else if (instance instanceof Collection) {
        instance.cards.filter(card => changed.has(card.product.handle)).forEach(card => {
          const prepared = card.prepared, frontPrepared = card.frontPrepared;
          card.prepared = false;
          card.frontPrepared = false;
          card.frontIntent = (card.frontIntent || 0) + 1;
          card.element.classList.remove('is-front-ready');
          if (prepared) instance.prepare(card);
          if (frontPrepared) instance.prepareFront(card);
        });
      }
    } catch { instances.delete(root); }
  };

  const discover = (scope = document) => {
    connectWaitlist();
    if (scope.matches?.('[data-drop-gallery]')) initialize(scope);
    scope.querySelectorAll('[data-drop-gallery]').forEach(initialize);
  };

  window.superDropGallery = { discover };
  document.addEventListener('shopify:section:load', (event) => discover(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [root, instance] of instances) {
      if (event.target.contains(root)) { instance?.destroy(); instances.delete(root); }
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => discover(), { once: true });
  else discover();
})();
